<template>
  <div>
    <v-container fluid grid-list-md>
      <h1>{{ $t(title) }}</h1>
      <v-data-table
        :headers="headers"
        :items="items"
        :rows-per-page-items="rowsPerPageItems"
        :pagination.sync="state.pagination"
        :total-items = "state.pagination.totalItems"
        must-sort
        @update:pagination="onUpdatePagination"
        class="elevation-1 mt-3 no-row-highlight"
      >
        <template slot="headerCell" slot-scope="props">
          <span>{{ $t(props.header.text) }}</span>
        </template>

        <template slot="items" slot-scope="props">
          <td
            v-for="header in headers.filter(x=>!x.default)"
            :key="header.value"
            :class="header.align ? 'text-xs-' + header.align : undefined"
          >
            {{getValue(props.item, header, $i18n.locale)}}
          </td>

          <td class="text-xs-center" v-if="editAction">
            <v-btn :flat="true" color="info" @click.stop="onOpenForm(props.item)" small style="min-width: 40px">
              <v-icon>{{editAction.icon}}</v-icon>
            </v-btn>
          </td>
          <td class="text-xs-center" v-if="deleteAction">
            <v-btn :flat="true" color="info" @click.stop="onDeleteClick(props.item)" small style="min-width: 40px">
              <v-icon>{{deleteAction.icon}}</v-icon>
            </v-btn>
          </td>
        </template>
      </v-data-table>
      
      <template v-if="createAction">
        <div class="mt-2">
          <v-btn color="success" @click="onOpenForm()">{{ $t(createAction.label) }}</v-btn>
        </div>
      </template>

      <ConfirmDialog
        v-if="openForm"
        @close="onFormClose"
        :width="formWidth"
        :messages="formMessages"
      >
        <v-container v-if="confirmationForm">
          <custom-form
            :formElements="confirmationForm"
            ref="form"
          />
        </v-container>
      </ConfirmDialog>
      <ConfirmDialog
        v-if="deleteConfirmOpen"
        @close="onDeleteConfirm"
        :messages="deleteMessages"
      >
      </ConfirmDialog>
    </v-container>
  </div>
</template>

<script>
import store from '../store';
import { createHeaders, mapItems } from '../utils/data-tables';
import VSubheader from 'vuetify/lib/components/VSubheader/VSubheader'
import VContent from 'vuetify/lib/components/VGrid/VContent'
import VLabel from 'vuetify/lib/components/VLabel/VLabel'
import ConfirmDialog from '../components/ConfirmDialog'
import CustomForm from '../components/CustomForm'

function findFormField(fields, fieldId) {
  if (!fields) {
    return null;
  }
  for (const item of fields) {
    if (item.id === fieldId) {
      return item;
    }
    const founded = findFormField(item.items, fieldId);
    if (founded) {
      return founded;
    }
  }
  return null;
}

export default {
  name: 'CRUDPage',
  components: {
    VSubheader,
    VContent,
    VLabel,
    ConfirmDialog,
    CustomForm
  },
  props: {
    config: Object,
  },
  computed: {
    state() {
      if(!store[this.config.type]) {
        store[this.config.type] = {
          pagination: {},
          filter: ''
        };
      }
      return store[this.config.type];
    },
    url() {
      return this.config.url;
    },
    title() { return this.config.title },
    actions() { return this.config.actions || []},
    createAction() { return this.findAction('create')},
    editAction() {  return this.findAction('edit')},
    deleteAction() {  return this.findAction('delete')},
    confirmationForm() {
      if (this.itemToEdit && this.config.form) {
        const formConfig = JSON.parse(JSON.stringify(this.config.form));
        
        for (const key in this.itemToEdit) {
          const field = findFormField(formConfig, key);
          if (field) {
            field.value = this.itemToEdit[key] || '';
          }
        }
        console.log('formConfig', formConfig);
        return formConfig;
      }
      return this.config.form
    },
    formWidth() { return this.config.formWidth},
    formMessages() {
      let messages = null;
      if (!this.itemToEdit) {
        messages = this.createAction && this.createAction.messages;
      } else {
        messages = this.editAction && this.editAction.messages;
      }
      if (!messages) {
          messages = {
          title: `${this.$i18n.t(!this.itemToEdit ? 'Action.add' : 'Action.edit')} ${this.title}`,
          cancel: this.$i18n.t('Action.cancel'),
          confirm: this.$i18n.t('Action.confirm'),
        }
      } else {
        Object.keys(messages).forEach(_ => messages[_] = this.$i18n.t(messages[_]));
      }
      return messages;
    },
    deleteMessages() {
      if (this.deleteAction && this.deleteAction.messages) {
        const messages = {...this.deleteAction.messages};
        Object.keys(messages).forEach(_ => messages[_] = this.$i18n.t(messages[_]));
        console.log('Messages', messages);
        return messages;
      }
      return {
        title: `${this.$i18n.t('Action.delete')} ${this.title}`,
        cancel: this.$i18n.t('Action.cancel'),
        confirm: this.$i18n.t('Action.confirm'),
      };
    },
    deleteConfirmOpen() {
      return !!this.deleteId;
    }
  },
  data() {
    const sourceMapper = {};
    const actions = this.config.actions ? this.config.actions
        .filter(_ => _.id != 'create')
        .map(_ => ({label: _.label, icon: _.icon}))
      : []

    const headers = createHeaders(
      this.config.columns,
      actions,
      sourceMapper
    );

    return {
      headers,
      sourceMapper,
      rowsPerPageItems: [12, 24, 48, 96, { "text": "$vuetify.dataIterator.rowsPerPageAll", "value": -1 }],
      items: [],
      openForm: false,
      deleteId: null,
      itemToEdit: null,
    };
  },
  async mounted() {
  },
  methods: {
    findAction(id) {
      return this.actions.find(_ => _.id === id);
    },
    async refresh() {
      const params = {
        offset: (this.state.pagination.page || 1) - 1,
        limit: this.state.pagination.rowsPerPage
      };
      if (this.state.pagination.sortBy) {
        params['orderby'] = (this.state.pagination.descending ? '-' : '') + this.state.pagination.sortBy;
      }
      if (this.state.filter) {
        params['search'] = this.state.filter;
      }

      try {
        const response = await this.$http.get(this.$root.route(this.url), {params});
        const items = mapItems(response.body.result.data, this.sourceMapper);
        this.items = items;
        this.state.pagination.totalItems = response.body.result.count;
      } catch (err) {
        console.error('Fail', err);
      }
    },
    onUpdatePagination(paginationObject) {
      Object.keys(paginationObject).forEach(key => this.state.pagination[key] = paginationObject[key]);
      this.refresh()
    },
    onFilterChanged() {
      this.$refs['filter'].isActive = false;
      this.refresh();
    },
    onSearchClick() {
      this.$refs['filter'].isActive = true;
    },
    getValue(item, header, i18Locale) {
      const res = item[header.value];
      if (header.type === 'num') {
        return res.toLocaleString(i18Locale, {minimumFractionDigits: 2, maximumFractionDigits: 2});
      }
      if (header.type === 'date') {
        return !res ? '-' : new Date(res * 1000).toLocaleDateString(
          i18Locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
      }
      return res;
    },

    onOpenForm(itemToEdit) {
      this.openForm = true;
      this.itemToEdit = itemToEdit;
    },

    prepareConfirmationForm(itemToEdit) {

    },
    async onFormClose(confirm) {
      if (confirm) {
        try {
          let keyValueList = [];
          if (this.$refs.form) {
            if (!this.$refs.form.validate()) {
              return;
            }
            keyValueList = await this.$refs.form.getData()
          }
          const data = (keyValueList || []).reduce((p, c) => {p[c.key] = c.value; return p;}, {});
          console.log('data', data);

          const formData = new FormData()
          for (let fieldName in data) {
            const value = data[fieldName]
            if (value instanceof File) {
              formData.append(fieldName, value, value.name)
            } else {
              formData.append(fieldName, value)
            }
          }

          if (this.itemToEdit) {
            await this.$http.post(this.$root.route(`${this.url}/${this.itemToEdit.ID}`), formData);
          } else {
            await this.$http.post(this.$root.route(this.url), formData);
          }
          this.openForm = false;
          this.itemToEdit = null;
          this.refresh();
        } catch(err) {
          console.error(err);
        }
        return;
      }
      this.openForm = false;
      this.itemToEdit = null;
    },

    onDeleteClick(item) {
      this.deleteId = item.ID;
    },
    async onDeleteConfirm(confirm) {
      if (confirm && this.deleteId) {
        try {
          await this.$http.delete(this.$root.route(`${this.url}/${this.deleteId}`));
          this.deleteId = null;
          this.refresh();
        } catch(err) {
          console.error(err);
        }
        return;
      } else {
        this.deleteId = null;
      }
    },
  }
}
</script>

<style lang="scss">
  .no-row-highlight tbody {
    tr:hover {
      background-color: transparent !important;
      cursor: default;
    }
  }
  .filter-column-editor + i {
    float: left;
    margin-left: 6px;
  }
</style>

<style lang="scss" scoped>
  .filter-column-editor {
    display: inline-block !important;
    width: 20px !important;
  }
  .filter-column-editor.active i {
    opacity: 1 !important;
  }
  .filter-column-editor i {
    transform: none !important;
    color: white !important;
    opacity: 0.5 !important;
  }
  span.filter-column-text {
    float: left;
  }
</style>
